// DS2.6A — read-only destination ranking. No IO, persistence or transfer quantity.
// A mature SKU/site can progress without waiting for every other site's evidence.
export const TRANSFER_EVIDENCE_POLICY = Object.freeze({
  minimumObservations: 4,
  minimumObservedDays: 3,
  sourceDeadDays: 14,
  recentMovementDays: 7,
  snapshotFreshDays: 7,
});

const DAY_MS = 86400000;
const scopeKey = (value) => String(value || '').trim().toLowerCase();
const numberOrNull = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};
const ageDays = (timestamp, nowMs) => {
  const atMs = Date.parse(timestamp || '');
  return Number.isFinite(atMs) && atMs <= nowMs ? (nowMs - atMs) / DAY_MS : null;
};

export function formatTransferMetric(value, suffix = '') {
  const amount = numberOrNull(value);
  if (amount === null) return '—';
  if (amount > 0 && amount < 0.01) return `<0.01${suffix}`;
  const digits = amount > 0 && amount < 0.1 ? 2 : 1;
  return `${Number(amount.toFixed(digits))}${suffix}`;
}

export function buildTransferIntelligence({ rows = [], sourceScope = '', evaluatedAt = '' } = {}) {
  const suppliedAt = Date.parse(evaluatedAt || '');
  const nowMs = Number.isFinite(suppliedAt) ? suppliedAt : Date.now();
  const allRows = Array.isArray(rows) ? rows : [];
  const wantedSource = scopeKey(sourceScope);
  const source = wantedSource ? allRows.find((row) => scopeKey(row.scope) === wantedSource) || null : null;
  const sourceSoh = numberOrNull(source?.soh);
  const sourceDays = numberOrNull(source?.daysStatic);
  const sourceSnapshotAge = ageDays(source?.latestSnapshotAt, nowMs);
  const blockers = [];

  if (!source) blockers.push('The selected site has no canonical source evidence for this SKU.');
  else {
    if (source.error || source.latestRevisionTrusted !== true) blockers.push('The source revision is not trusted.');
    if (sourceSoh === null || sourceSoh <= 0) blockers.push('The source has no positive SOH to review.');
    if (source.networkSignal !== 'DEAD' || sourceDays === null || sourceDays < TRANSFER_EVIDENCE_POLICY.sourceDeadDays) {
      blockers.push('Source ranking requires the conservative network DEAD signal: 14+ days without movement.');
    }
    if (sourceSnapshotAge === null || sourceSnapshotAge >= TRANSFER_EVIDENCE_POLICY.snapshotFreshDays) {
      blockers.push('A source inventory snapshot from the last 7 days is required. Opening this modal does not refresh stock evidence.');
    }
  }

  const ranked = [];
  const developing = [];
  const excluded = [];
  const seenScopes = new Set();
  for (const row of allRows) {
    const key = scopeKey(row?.scope);
    if (!key || key === wantedSource || seenScopes.has(key)) continue;
    seenScopes.add(key);
    const soh = numberOrNull(row.soh);
    const movementAge = ageDays(row.lastObservedMovement, nowMs);
    const snapshotAge = ageDays(row.latestSnapshotAt, nowMs);
    let exclusion = '';
    if (row.error || row.networkSignal === 'ERROR') exclusion = 'Network evidence could not be loaded.';
    else if (soh === null) exclusion = 'No current numeric SOH observation.';
    else if (soh <= 0 || row.networkSignal === 'OUT_OF_STOCK') exclusion = 'Out of stock; excluded from this redistribution ranking.';
    else if (row.latestRevisionTrusted !== true) exclusion = 'Latest revision is not trusted.';
    else if (row.networkSignal !== 'MOVING') exclusion = `${String(row.networkSignal || 'NO_EVIDENCE').replaceAll('_', ' ')} is not a proven recently moving destination.`;
    else if (!row.hasObservedMovement || !(Number(row.movementRevision) > 0) || movementAge === null || movementAge >= TRANSFER_EVIDENCE_POLICY.recentMovementDays) {
      exclusion = 'No genuine canonical SOH transition in the last 7 days.';
    } else if (snapshotAge === null || snapshotAge >= TRANSFER_EVIDENCE_POLICY.snapshotFreshDays) {
      exclusion = 'Current inventory snapshot is missing, future-dated or 7+ days old.';
    }
    if (exclusion) {
      excluded.push({ ...row, reason: exclusion });
      continue;
    }

    const velocity = row.velocity || {};
    const observations = Math.max(0, Math.floor(numberOrNull(velocity.observations) || 0));
    const observedDays = Math.max(0, numberOrNull(velocity.observedDaysExact ?? velocity.observedDays) || 0);
    const depletedUnits = Math.max(0, numberOrNull(velocity.depletedUnits) || 0);
    const replenishedUnits = Math.max(0, numberOrNull(velocity.replenishedUnits) || 0);
    const depletionAge = ageDays(velocity.lastDepletionAt, nowMs);
    // Recompute from the witnessed path, not a rounded display rate or a claim
    // of confidence. An upward transition alone proves movement, not demand.
    const depletionPerDay = observedDays > 0 ? depletedUnits / observedDays : 0;
    const cover = depletionPerDay > 0 ? soh / depletionPerDay : null;
    const stockCoverDays = cover !== null && Number.isFinite(cover) ? cover : null;
    const mature = observations >= TRANSFER_EVIDENCE_POLICY.minimumObservations
      && observedDays >= TRANSFER_EVIDENCE_POLICY.minimumObservedDays;
    const recentDepletion = depletionAge !== null && depletionAge < TRANSFER_EVIDENCE_POLICY.recentMovementDays;
    const eligible = mature && recentDepletion && depletedUnits > 0 && depletionPerDay > 0 && stockCoverDays !== null;
    const reasons = [
      `${observations} distinct SOH observations over ${formatTransferMetric(observedDays)} days.`,
      depletedUnits > 0 ? `${formatTransferMetric(depletionPerDay, '/day')} observed depletion; ${formatTransferMetric(depletedUnits)} units depleted.` : 'Recent movement exists, but no downward depletion has been observed.',
      `Current SOH ${formatTransferMetric(soh)}${stockCoverDays !== null ? `; about ${formatTransferMetric(stockCoverDays)} days of historical-rate stock cover` : '; stock cover cannot yet be estimated'}.`,
      `Last genuine movement ${formatTransferMetric(movementAge)} days ago; inventory snapshot ${formatTransferMetric(snapshotAge)} days old.`,
      depletionAge !== null ? `Last observed downward depletion ${formatTransferMetric(depletionAge)} days ago.` : 'A timestamp for recent downward depletion is not yet proven.',
    ];
    if (replenishedUnits > 0) reasons.push(`${formatTransferMetric(replenishedUnits)} units of upward SOH changes observed; current SOH already includes replenishment/adjustments.`);
    const needs = [];
    if (observations < TRANSFER_EVIDENCE_POLICY.minimumObservations) needs.push(`${observations}/4 distinct observations`);
    if (observedDays < TRANSFER_EVIDENCE_POLICY.minimumObservedDays) needs.push(`${formatTransferMetric(observedDays)}/3 days of evidence`);
    if (depletedUnits <= 0) needs.push('no observed depletion yet');
    else if (!recentDepletion) needs.push('downward depletion in the last 7 days is not proven');
    const candidate = {
      ...row, observations, observedDays, depletedUnits, replenishedUnits,
      depletionPerDay, stockCoverDays, movementAge, depletionAge, snapshotAge, reasons,
      confidence: eligible ? 'ESTABLISHED' : 'DEVELOPING',
      holdReason: eligible ? '' : needs.length ? needs.join(' · ') : 'Depletion/cover evidence is not usable yet.',
    };
    (eligible ? ranked : developing).push(candidate);
  }

  // Transparent ordering, not an opaque score or a highest-velocity shortcut.
  // Current stock cover leads; depletion and recency only break comparable ties.
  ranked.sort((a, b) => a.stockCoverDays - b.stockCoverDays
    || b.depletionPerDay - a.depletionPerDay
    || a.movementAge - b.movementAge
    || b.observations - a.observations
    || b.observedDays - a.observedDays
    || String(a.site).localeCompare(String(b.site))
    || scopeKey(a.scope).localeCompare(scopeKey(b.scope)));
  const byName = (a, b) => String(a.site).localeCompare(String(b.site)) || scopeKey(a.scope).localeCompare(scopeKey(b.scope));
  developing.sort(byName);
  excluded.sort(byName);

  return {
    source, sourceSoh, sourceDays, blockers, sourceEligible: blockers.length === 0,
    // Weak/stale sources may still inspect the network table, but never obtain
    // a transfer ranking from a destination's stronger evidence alone.
    ranked: blockers.length ? [] : ranked.map((row, index) => ({ ...row, rank: index + 1 })),
    developing: blockers.length ? [] : developing,
    excluded,
    networkMoving: allRows.filter((row) => row.networkSignal === 'MOVING').length,
    networkDead: allRows.filter((row) => row.networkSignal === 'DEAD').length,
    quantityRecommendation: null,
    rankingRule: 'Established evidence first. Lower estimated stock cover, then higher observed depletion, more recent movement and stronger evidence. No quantity recommendation.',
  };
}
